import * as React from "react";

import "./demographics.scss";

import {cs}                     from "@Common/react/chain-services";
import {consumeContext}         from "@Common/react/context";
import {keyed}                  from "@Common/react/keyed";
import {stateChangeNestedValue} from "@Common/react/state-change-nested-value";
import {UseState}               from "@Common/react/use-state";

import {ConfirmCancel} from "../common/confirm-cancel";
import {WizardControl} from "../common/wizard-control";

import {Step1SelectTarget}    from "./step-1-select-target/step-1-select-target";
import {Step2Customize}       from "./step-2-customize/step-2-customize";
import {Step3NarrowingSearch} from "./step-3-narrowing-search/step-3-narrowing-search";
import {Step4Finalize}        from "./step-4-finalize/step-4-finalize";

export const Demographics = ({entityId, onClose, onCreated, onCancel, cancelLabel="Cancel", next}) => cs(
    consumeContext("apis"),
    ["confirmCancel", (_, next) => ConfirmCancel({next})],
    ["query", ({}, next) => UseState({initValue: null, next})],
    ["state", ({}, next) => UseState({initValue: null, next})],
    ["wizardControl", ({state, apis, query}, next) => WizardControl({
        steps: [
            {
                render: () => (
                    Step1SelectTarget({
                        onSelect: async (idSearch) => {
                            const customizedInputsConfig = await apis.next.mailing.demographics.getCustomizedInputsConfig(idSearch);
                            stateChangeNestedValue(state, [], () => ({idSearch, customizedInputsConfig}));
                        },
                        onCancel,
                        cancelLabel,
                        entityId,
                    })
                ),
            },
            {
                condition: () => state.value,
                render: ({resetNav}) => (
                    Step2Customize({
                        initCustomization: state.value.inputs?.customization,
                        config: state.value.customizedInputsConfig,
                        onBack: () => state.onChange(null),
                        onNext: (customization) => {
                            stateChangeNestedValue(state, ["inputs"], () => ({customization}));
                            resetNav();
                        },
                        query,
                    })
                ),
            },
            {
                condition: () => state.value?.inputs,
                render: ({goBack, resetNav}) => (
                    Step3NarrowingSearch({
                        initState: state.value.inputs.narrowing,
                        onReselectTarget: () => state.onChange(null),
                        onBack: goBack,
                        onSearch: ({searchType, geography}) => apis.next.mailing.demographics.search({
                            idSearch: state.value.idSearch,
                            selectedTargets: state.value.inputs.customization,
                            searchType, geography,
                        }),

                        onNext: ({searchResult, mailOptions}) => {
                            stateChangeNestedValue(state, ["inputs", "narrowing"], () => ({searchResult, mailOptions}));
                            resetNav();
                        },
                        query
                    })
                ),
            },
            {
                condition: () => state.value?.inputs?.narrowing,
                render: ({goBack}) => (
                    Step4Finalize({
                        total: (
                            state.value.inputs.narrowing.mailOptions.mailCount ??
                            state.value.inputs.narrowing.searchResult.total
                        ),
                        onBack: goBack,
                        onComplete: async (finalize) => {
                            const result = await apis.next.mailing.demographics.createMailingList({
                                idSearch: state.value.idSearch,
                                inputsCustomization: state.value.inputs.customization,
                                narrowing: state.value.inputs.narrowing,
                                finalize,
                                entityId,
                            })

                            onCreated([result.mailingListId ?? result.idList]);
                            onClose();
                        },
                    })
                ),
            },
        ],
        next,
    })],
    ({state, confirmCancel, wizardControl}) => (
        next({
            onPassiveClose: state.value && confirmCancel,
            content: cs(
                keyed(wizardControl.currentStep.index),
                () => wizardControl.currentStep.render(),
            ),
        })
    )
);
